import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { useCloseBillingOrganization } from '@/hooks/actions/use-close-billing-organization';
import { useBillingSettingsQuery } from '@/hooks/queries/use-billing-settings-query';
import SettingsSection from '@/pages/settings/components/SettingsSection';
import { Button } from '@chakra-ui/react';
import type { OrganizationBillingContext } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function OrganizationClosureSection({ context }: { context: OrganizationBillingContext }) {
  const { t } = useTranslation();
  const query = useBillingSettingsQuery({ context });
  const closure = useCloseBillingOrganization(query.data);
  const [open, setOpen] = useState(false);
  const closed = !!query.data?.closedAt;
  const pending = !closed && closure.data?.status === 'pending';
  const authorized =
    (context.channel === 'stripe' || context.channel === 'none') &&
    query.isSuccess &&
    !query.error &&
    !!query.data?.canCloseOrganization;
  if (!closed && !pending && !authorized) return null;
  const busy = query.isFetching || closure.isPending;
  return (
    <SettingsSection
      title={t('billing.settings.closureTitle')}
      description={t('billing.settings.closureDescription')}
      isLast
    >
      {closed ? (
        <NoticePanel tone="info" title={t('billing.settings.closureClosed')}>
          {t('billing.settings.closureClosedDescription')}
        </NoticePanel>
      ) : pending ? (
        <NoticePanel
          tone="info"
          title={t('billing.settings.closurePending')}
          actions={
            <Button
              variant="outline"
              loading={query.isFetching}
              onClick={() => void query.refetch()}
            >
              {t('billing.settings.closureCheck')}
            </Button>
          }
        >
          {t('billing.settings.closurePendingDescription')}
        </NoticePanel>
      ) : (
        <>
          <Button
            variant="outline"
            colorPalette="red"
            disabled={busy}
            onClick={() => setOpen(true)}
          >
            {t('billing.settings.closureAction')}
          </Button>
          <ConfirmDialog
            isOpen={open}
            title={t('billing.settings.closureConfirmTitle')}
            description={t('billing.settings.closureConfirmDescription')}
            confirmText={t('billing.settings.closureConfirm')}
            confirmColorPalette="red"
            isLoading={closure.isPending}
            isConfirmDisabled={busy || !authorized}
            showCloseTrigger
            onCancel={() => setOpen(false)}
            onConfirm={() => {
              if (busy || !authorized) return;
              void closure
                .mutateAsync()
                .then(() => setOpen(false))
                .catch(() => undefined);
            }}
          />
        </>
      )}
    </SettingsSection>
  );
}
